"use client";

import { c as _c } from "react/compiler-runtime";
import { ChangeEvent, Suspense, useState } from "react";
import { useRouter } from "next/navigation";
import { chakra, Input, InputGroup } from "@chakra-ui/react";
import { IoSearchSharp } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import DynamicAddressAutofill, { AddressAutofillOptions, AddressAutofillRetrieveResponse } from "./address-autofill";
import { AddressAutofillSuggestionResponse } from "@mapbox/search-js-core";
const autofillOptions: AddressAutofillOptions = {
  country: "US",
  limit: 10,
  bbox: [-122.55, 37.69, -122.35, 37.83],
  proximity: {
    lng: -122.4194,
    lat: 37.7749
  },
  streets: false,
  language: "en"
};

// NOTE: UI changes to this page ought to be reflected in its suspense skeleton `search-bar-skeleton.tsx` and vice versa
// TODO: isolate the usage of `useSearchParams()` so that the Suspense boundary can be even more narrow if possible
interface SearchBarProps {
  inputAddress: string;
  onInputAddressChange: (address: string) => void;
  onSearchChange: (coords: number[], address: string) => void;
}
const SearchBar = t0 => {
  const $ = _c(29);
  const {
    inputAddress,
    onInputAddressChange,
    onSearchChange
  } = t0;
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const [, setSuggestionsAvailable] = useState(false);
  const router = useRouter();
  let t1;
  if ($[0] !== onInputAddressChange || $[1] !== router) {
    t1 = () => {
      onInputAddressChange("");
      setSuggestionSelected(false);
      router.push("/", {
        scroll: false
      });
    };
    $[0] = onInputAddressChange;
    $[1] = router;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleClearClick = t1;
  let t2;
  if ($[3] !== onSearchChange) {
    t2 = res => {
      const addressData = res.features[0];
      const addressLine = res.features[0].properties.feature_name;
      const coords = addressData.geometry.coordinates;
      onSearchChange(coords, addressLine);
      setSuggestionSelected(true);
    };
    $[3] = onSearchChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleRetrieve = t2;
  let t3;
  if ($[5] !== inputAddress.length || $[6] !== onInputAddressChange || $[7] !== suggestionSelected) {
    t3 = event => {
      onInputAddressChange(event.currentTarget.value);
      if (!suggestionSelected) {
        setSuggestionsAvailable(false);
      } else {
        if (suggestionSelected && inputAddress.length <= 3) {
          setSuggestionSelected(false);
          setSuggestionsAvailable(false);
        }
      }
    };
    $[5] = inputAddress.length;
    $[6] = onInputAddressChange;
    $[7] = suggestionSelected;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleAddressChange = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = res_0 => {
      setSuggestionsAvailable(res_0.suggestions.length > 0);
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleSuggest = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      base: "full",
      sm: "xs",
      md: "sm"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IoSearchSharp color="grey.900" fontSize="1.1em" size="20" data-testid="search-icon" />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== handleClearClick || $[13] !== inputAddress.length) {
    t7 = inputAddress.length !== 0 && <RxCross2 color="grey.900" fontSize="1.1em" size="20" data-testid="clear-icon" onClick={handleClearClick} />;
    $[12] = handleClearClick;
    $[13] = inputAddress.length;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      base: "lg",
      md: "xl",
      xl: "xl"
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      base: "9",
      md: "12"
    };
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      borderColor: "yellow"
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      borderColor: "yellow",
      _placeholder: {
        color: "grey.900"
      }
    };
    t12 = {
      borderColor: "red"
    };
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  let t13;
  if ($[20] !== handleAddressChange || $[21] !== inputAddress) {
    t13 = <Input autoFocus={true} placeholder="Search San Francisco address" fontFamily="body" fontSize="md" size={t8} pt="0" pr="2.5" pb="0" pl={t9} borderRadius="full" border="search" bgColor="white" shadow="search" type="text" name="address-1" value={inputAddress} onChange={handleAddressChange} _focus={t10} _hover={t11} _invalid={t12} autoComplete="address-line1" />;
    $[20] = handleAddressChange;
    $[21] = inputAddress;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13 || $[24] !== t7) {
    t14 = <InputGroup w={t5} data-testid="search-bar" startElement={t6} endElement={t7}>{t13}</InputGroup>;
    $[23] = t13;
    $[24] = t7;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== handleRetrieve || $[27] !== t14) {
    t15 = <chakra.form position="relative"><Suspense><DynamicAddressAutofill accessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN ?? ""} options={autofillOptions} onRetrieve={handleRetrieve} onSuggest={handleSuggest}>{t14}</DynamicAddressAutofill></Suspense></chakra.form>;
    $[26] = handleRetrieve;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  return t15;
};
export default SearchBar;